import Core from '../plugin-core-system/core.class.js';
import TranslatedLang from '../helper/translated-lang.helper.js';
import Splide from '@splidejs/splide';
import CssProperties from '../helper/css-properties.helper.js';


export default class CsSplide extends Core {

  static options = {
    sliderConfigBase: {
      type: 'loop',
      lazyLoad: 'nearby',
      classes: {
        arrow: 'button--block-icon splide__arrow cs-icon-angle-double-left',
      },
      pagination: false,
      autoplay: false,
      interval: 7000,
    },
    sliderConfigTeaser: {
      gap: CssProperties.getCssPixelProperty('--slider-item-space-x'),
      arrows: true,
      pagination: true
    },
    sliderConfigProgression:{
      perPage: 1,
      autoplay: true,
      interval: 3000,
    },
    sliderConfigMultipleItems:{
      perPage: 3,
      gap: CssProperties.getCssPixelProperty('--slider-item-space-x'),
      breakpoints: {
        640: {
          perPage: 2,
        },
        480: {
          perPage: 1,
        },
      },
    },
    sliderConfigCol3: {
      perPage: 3,
      breakpoints: {
        1200: {
          perPage: 2
        },
        820: {
          perPage: 1
        }
      }
    },
    sliderConfigCol2: {
      perPage: 2,
      breakpoints: {
        820: {
          perPage: 1
        }
      }
    },

    sliderDefaultI18n: {
      i18n: {
        prev: 'Previous Slide',
        next: 'Next Slide',
        first: 'Go to the first slide',
        last: 'Go to the last slide',
        slideX: 'Go to slide %s',
        pageX: 'Go to page %s',
        play: 'Start autoplay',
        pause: 'Pause autoplay',
        carousel: 'Slider',
        slide: 'Slide',
        select: 'Select a slide to display',
        slideLabel: '%s of %s'
      }
    }
  };

  initializeSplide(options = {}) {
    const splide = new Splide(this.el, options);
    splide.mount();
  }

  initializeProgressSlider(options) {
    const splide = new Splide(this.el, options);

    splide.on('move', () => { //after the movement
      requestAnimationFrame(() => { //waits for the class is-active to be reset
        const progressPagination = document.querySelector('.slider-progress');
        this.updateProgressBar(splide, progressPagination);
      });
    });

    splide.on('mounted', () => { //after loading
      const progressPagination = document.querySelector('.slider-progress');
      this.initializeProgressBars(progressPagination);
      this.updateProgressBar(splide, progressPagination);
      this.startStopAnimation(splide);
    });

    splide.mount();
  }

  // method for initializing progress bars
  initializeProgressBars(progressPagination) {
    const buttons = progressPagination.querySelectorAll('.splide__pagination__page');
    buttons.forEach(button => {
      const bar = document.createElement('div');
      bar.className = 'progress-bar';
      button.appendChild(bar);
    });
  }

  // progress bar update method
  updateProgressBar(splideInstance, progressPagination) {
    const buttons = progressPagination.querySelectorAll('.splide__pagination__page');
    const autoplay = splideInstance.Components.Autoplay;

    buttons.forEach(button => {
      const progressBar = button.querySelector('.progress-bar');
      if (progressBar) {
        if (button.classList.contains('is-active')) {
          if (autoplay.isPaused()) {
            // When autoplay is paused, immediately jump to 100% without animation
            progressBar.style.transition = 'none';
            progressBar.style.width = '100%';
          } else {
            // If autoplay is not paused, use normal animation
            setTimeout(() => {
              progressBar.style.transition = `width ${splideInstance.options.interval}ms linear`;
              progressBar.style.width = '100%';
            }, 0); // start immediately
          }
        } else {
          // progress bar for other slides
          progressBar.style.transition = 'none';
          progressBar.style.width = '0';
        }
      }
    });
  }


  startStopAnimation(splide) {
    // waiting for event from the accessibility animation
    document.addEventListener('splideSlidersFound', function(e) {
      const autoplay = splide.Components.Autoplay;
      // check if the value of e.detail.found is true
      if (e.detail.found === true) {
        if (autoplay.isPaused()) {
          autoplay.play();
        }
      } else {
        // if the value of e.detail.found is false
        autoplay.pause();
      }
    });

  }

  initSplideWidthBreakpoints(options) {
    const splide = new Splide(this.el, options);

    splide.mount();

    const slidesCount = splide.Components.Slides.getLength();
    let perPage = splide.options.perPage; // changes on different breakpoints

    splide.on('resize', () => {
      const perPageDiff = splide.options.perPage - perPage;

      if (splide.options.type !== 'loop') {
        if (perPageDiff !== 0) {
          perPage = splide.options.perPage;

          const index = splide.index;
          const positions = slidesCount - perPage + 1;

          if (index >= positions) {
            splide.Components.Controller.setIndex(positions - 1);
          }
        }
      }
    });
  }

  init() {
    const base = CsSplide.options.sliderConfigBase;

    let sliderOptions = {
      ...base,
    };

    try {
      const langConfig = TranslatedLang.translatedLangConfig('splide', 'i18n');
      sliderOptions = {
        ...sliderOptions,
        i18n: { ...(langConfig?.i18n || {}) },
      };
    } catch {
      sliderOptions = {
        ...sliderOptions,
        i18n: { ...(CsSplide.options.sliderDefaultI18n?.i18n || {}) },
      };
    }

    if (this.el.matches('.js-teaser-slider')) {
      const teaser = CsSplide.options.sliderConfigTeaser;

      sliderOptions = {
        ...sliderOptions,
        ...teaser,
        breakpoints: {
          ...(sliderOptions.breakpoints || {}),
          ...(teaser?.breakpoints || {}),
        },
      };

      // check if element has data-cols and merge slider config for value
      if (this.el.hasAttribute('data-cols')) {
        const cols = this.el.getAttribute('data-cols');
        const colConfig = CsSplide.options[`sliderConfigCol${cols}`];
        if (colConfig) {
          sliderOptions = {
            ...sliderOptions,
            ...colConfig,
            breakpoints: {
              ...(sliderOptions.breakpoints || {}),
              ...(colConfig?.breakpoints || {}),
            },
          };
        }
      }

      this.initSplideWidthBreakpoints(sliderOptions);

    } else if (this.el.matches('.js-slider-progress')) {
      sliderOptions = {
        ...sliderOptions,
        ...CsSplide.options.sliderConfigProgression,
      };

      this.initializeProgressSlider(sliderOptions);

    } else if (this.el.matches('.js-slider-multiple-items')) {
      const multi = CsSplide.options.sliderConfigMultipleItems;
      sliderOptions = {
        ...sliderOptions,
        ...multi,
        breakpoints: { ...(multi.breakpoints || {}) }, // key part: avoid shared ref
      };

      this.initSplideWidthBreakpoints(sliderOptions);

    } else {
      this.initializeSplide(sliderOptions);
    }
  }
}
